import React, { useState, useEffect } from "react";
import { Card, message, Spin } from "antd";
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    Cell,
} from "recharts";
import { api } from "../../../Services/api";
import { useSelector } from "react-redux";
import { LeftOutlined, RightOutlined } from "@ant-design/icons";

const StatusSummaryChart = ({ projectId }) => {
    const [loading, setLoading] = useState(false);
    const [chartData, setChartData] = useState([]);
    const [currentWeekStart, setCurrentWeekStart] = useState(getWeekStart(new Date()));

    const trackingClientId = useSelector(
        (state) => state.Auth.user.trackingclientid
    );

    // ✅ Get Monday as start of week
    function getWeekStart(date) {
        const day = date.getDay();
        const diff = day === 0 ? -6 : 1 - day;
        const monday = new Date(date);
        monday.setDate(date.getDate() + diff);
        monday.setHours(0, 0, 0, 0);
        return monday;
    }

    const formatDate = (date) => date.toISOString().split("T")[0];

    // ✅ Current week range
    const getDateRange = () => {
        const startDate = new Date(currentWeekStart);
        const endDate = new Date(startDate);
        endDate.setDate(startDate.getDate() + 6);
        return { startdate: formatDate(startDate), enddate: formatDate(endDate) };
    };

    // ✅ Week label (e.g., Date: 01 Nov 27 - 07 Nov 27)
    const formatWeekLabel = () => {
        const start = new Date(currentWeekStart);
        const end = new Date(currentWeekStart);
        end.setDate(start.getDate() + 6);

        const formatDay = (date) => {
            const day = String(date.getDate()).padStart(2, "0");
            const month = date.toLocaleString("en-US", { month: "short" });
            const year = String(date.getFullYear()).slice(-2);
            return `${day} ${month} ${year}`;
        };

        return `Date: ${formatDay(start)} - ${formatDay(end)}`;
    };

    // ✅ Fetch API data
    const fetchStatusData = async () => {
        setLoading(true);
        try {
            const { startdate, enddate } = getDateRange();
            const payload = {
                projectid: projectId,
                startdate,
                enddate,
                trackingclientid: trackingClientId,
            };

            const response = await api.post(
                "taskpercentage/gettaskstatustrackdata",
                payload,
                {
                    headers: { authorizationToken: localStorage.getItem("token") },
                }
            );

            const data = response.data;

            if (data?.statusCounts && Object.keys(data.statusCounts).length > 0) {
                const formattedData = Object.entries(data.statusCounts).map(
                    ([status, count]) => ({
                        name: status,
                        count,
                    })
                );
                setChartData(formattedData);
            } else {
                setChartData([]);
            }
        } catch (error) {
            console.error("Error fetching status summary:", error);
            //   message.error("Failed to load status summary");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchStatusData();
    }, [currentWeekStart]);

    // ✅ Week navigation
    const goToPreviousWeek = () => {
        const newWeek = new Date(currentWeekStart);
        newWeek.setDate(currentWeekStart.getDate() - 7);
        setCurrentWeekStart(getWeekStart(newWeek));
    };

    const goToNextWeek = () => {
        const newWeek = new Date(currentWeekStart);
        newWeek.setDate(currentWeekStart.getDate() + 7);
        const nextMonday = getWeekStart(newWeek);
        if (nextMonday > getWeekStart(new Date())) return;
        setCurrentWeekStart(nextMonday);
    };

    const isFutureWeek =
        getWeekStart(currentWeekStart).getTime() >=
        getWeekStart(new Date()).getTime();

    // 🎨 Assign unique colors per status
    const COLORS = [
        "#36A2EB", // Blue
        "#FF6384", // Red
        "#FFCE56", // Yellow
        "#4BC0C0", // Teal
        "#9966FF", // Purple
        "#FF9F40", // Orange
        "#00C49F", // Green
    ];

    const [windowWidth, setWindowWidth] = useState(window.innerWidth);

    useEffect(() => {
        const handleResize = () => setWindowWidth(window.innerWidth);
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);


    return (
        <Card
            title={<span style={{ color: "#fff" }}>Status Summary Overview</span>}
            bordered={false}
            style={{
                background: "#3E3F3E",
                borderRadius: 10,
                color: "#fff",
                boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
            }}
            extra={
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "15px",
                        color: "#fff",
                    }}
                >
                    <LeftOutlined
                        onClick={goToPreviousWeek}
                        style={{ cursor: "pointer", fontSize: 18 }}
                    />
                    <span
                        style={{
                            fontWeight: 500,
                            minWidth: 220,
                            textAlign: "center",
                            letterSpacing: 0.5,
                        }}
                    >
                        {formatWeekLabel()}
                    </span>
                    <RightOutlined
                        onClick={goToNextWeek}
                        style={{
                            cursor: isFutureWeek ? "not-allowed" : "pointer",
                            opacity: isFutureWeek ? 0.5 : 1,
                            fontSize: 18,
                        }}
                    />
                </div>
            }
        >
            <div
                style={{
                    height: 320,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                }}
            >
                {loading ? (
                    <Spin size="large" />
                ) : chartData.length > 0 ? (
                    <>
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart
                                data={chartData}
                                barCategoryGap="40%" // Reduced for smaller widths
                                margin={{ top: 20, right: 30, left: 0, bottom: 5 }}
                            >
                                <defs>
                                    {chartData.map((entry, index) => (
                                        <linearGradient
                                            key={`grad-${index}`}
                                            id={`gradColor-${index}`}
                                            x1="0"
                                            y1="0"
                                            x2="0"
                                            y2="1"
                                        >
                                            <stop
                                                offset="0%"
                                                stopColor={COLORS[index % COLORS.length]}
                                                stopOpacity={0.95}
                                            />
                                            <stop
                                                offset="100%"
                                                stopColor={COLORS[(index + 1) % COLORS.length]}
                                                stopOpacity={0.75}
                                            />
                                        </linearGradient>
                                    ))}
                                </defs>

                                <CartesianGrid stroke="rgba(255,255,255,0.2)" strokeDasharray="6 6" />

                                <XAxis
                                    dataKey="name"
                                    tick={{ fill: "#fff", fontSize: 12 }}
                                    interval={0}
                                    tickFormatter={(value) =>
                                        value.length > 5 ? value.slice(0, 5) + "…" : value
                                    }
                                />


                                <YAxis tick={{ fill: "#fff", fontSize: 12 }} />

                                <Tooltip
                                    contentStyle={{
                                        backgroundColor: "#2f2f2f",
                                        borderRadius: "8px",
                                        border: "1px solid rgba(255,255,255,0.2)",
                                        color: "#fff",
                                        padding: "10px 12px",
                                    }}
                                    formatter={(value, name, props) => [
                                        `${value}`,
                                        `${props.payload.name}`, // heading
                                    ]}
                                    labelFormatter={() => ""} // disables duplicate header line
                                    itemStyle={{
                                        color: "#fff",
                                        fontWeight: 500,
                                        fontSize: "13px",
                                        marginTop: "4px",
                                    }}
                                    content={({ active, payload }) => {
                                        if (active && payload && payload.length) {
                                            const { name, count } = payload[0].payload;
                                            return (
                                                <div
                                                    style={{
                                                        backgroundColor: "#2f2f2f",
                                                        borderRadius: "8px",
                                                        border: "1px solid rgba(255,255,255,0.2)",
                                                        color: "#fff",
                                                        padding: "10px 12px",
                                                        boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
                                                    }}
                                                >
                                                    <div
                                                        style={{
                                                            fontWeight: 500,
                                                            fontSize: "12px",
                                                            color: "#dbd1d1ff",
                                                            marginBottom: "5px",
                                                        }}
                                                    >
                                                        {name}
                                                    </div>
                                                    <div style={{ fontWeight: 500, color: "#fff" }}>
                                                        {`${name} : ${count}`}
                                                    </div>
                                                </div>
                                            );
                                        }
                                        return null;
                                    }}
                                />

                                <Bar
                                    dataKey="count"
                                    barSize={window.innerWidth < 600 ? 30 : 40} // 👈 smaller bars for small devices
                                    radius={[6, 6, 0, 0]}
                                    animationDuration={900}
                                    animationEasing="ease-in-out"
                                >
                                    {chartData.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={`url(#gradColor-${index})`} />
                                    ))}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>

                        {/* ✅ Gradient Color Legend Below the Chart */}
                        <div
                            style={{
                                display: "flex",
                                justifyContent: "center",
                                gap: "20px",
                                flexWrap: "wrap",
                                marginTop: "12px",
                            }}
                        >
                            {chartData.map((entry, index) => {
                                const color1 = COLORS[index % COLORS.length];
                                const color2 = COLORS[(index + 1) % COLORS.length];
                                return (
                                    <div
                                        key={entry.name}
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            color: "#fff",
                                            fontSize: 12,
                                            fontWeight: 400,
                                        }}
                                    >
                                        <div
                                            style={{
                                                width: 14,
                                                height: 11,
                                                background: `linear-gradient(180deg, ${color1} 0%, ${color2} 100%)`,
                                                borderRadius: 1,
                                                marginRight: 8,
                                                boxShadow: "0 0 4px rgba(0,0,0,0.3)",
                                            }}
                                        />
                                        {entry.name}
                                    </div>
                                );
                            })}
                        </div>
                    </>
                ) : (
                    <div style={{ textAlign: "center", color: "#ccc", fontSize: 15 }}>
                        No status summary data available for this week
                    </div>
                )}
            </div>
        </Card>
    );
};

export default StatusSummaryChart;
